// Data + deterministic generation logic for the AI Agent Loop Builder.
// Everything here operationalizes the loop-engineering pillar
// (content/blog/loop-engineering-guide-2026.md): the verifier is the
// bottleneck, "done" is defined in measurable passes pinned up front,
// every loop gets a hard stop condition and a budget cap, and a loop
// without those is a doom loop waiting to fire.

export type Cadence = 'one-shot' | 'recurring'
export type Risk = 'low' | 'medium' | 'high'

export type VerifierCheck = {
  check: string
  how: string
  grade: 'hard' | 'judgment'
}

export type TaskType = {
  id: string
  label: string
  icon: string
  description: string
  goal: string
  loopSteps: [string, string, string, string] // discover, plan, execute, verify
  verifier: VerifierCheck[]
  tripwires: string[]
  handback: string
  recurringTrigger: string
}

export type RiskConfig = {
  id: Risk
  label: string
  description: string
  iterationCap: number
  perRunUsd: number
  monthlyUsd: number
  shipRule: string
}

export type CadenceConfig = {
  id: Cadence
  label: string
  description: string
}

export type LoopKit = {
  task: TaskType
  cadence: CadenceConfig
  risk: RiskConfig
  loopType: string
  loopPrompt: string
}

export const CADENCES: CadenceConfig[] = [
  {
    id: 'one-shot',
    label: 'One-shot',
    description: 'You kick it off, it runs to done once',
  },
  {
    id: 'recurring',
    label: 'Scheduled / recurring',
    description: 'A trigger wakes it without you - cron, webhook, another agent',
  },
]

export const RISK_LEVELS: RiskConfig[] = [
  {
    id: 'low',
    label: 'Low',
    description: 'Reversible output. A bad run costs you a review, nothing more.',
    iterationCap: 8,
    perRunUsd: 10,
    monthlyUsd: 300,
    shipRule:
      'All checks green = ship automatically. Spot-check one run in five to keep the verifier honest.',
  },
  {
    id: 'medium',
    label: 'Medium',
    description: 'User-facing or shared output. A bad run embarrasses you before you catch it.',
    iterationCap: 5,
    perRunUsd: 5,
    monthlyUsd: 150,
    shipRule:
      'All checks green earns a review request, not a ship. A human approves the final artifact before it goes anywhere.',
  },
  {
    id: 'high',
    label: 'High',
    description: 'Production data, money, infra, or anything irreversible.',
    iterationCap: 3,
    perRunUsd: 3,
    monthlyUsd: 90,
    shipRule:
      'Dry-run or draft mode only. A human approves every mutating action before it executes. Green checks mean "ready for review" - never "shipped".',
  },
]

export const TASK_TYPES: TaskType[] = [
  {
    id: 'code-refactor',
    label: 'Code refactor / migration',
    icon: '🔁',
    description: 'Restructure code or migrate APIs without changing behavior',
    goal: 'Refactor or migrate the target module in mechanical steps, without changing observable behavior.',
    loopSteps: [
      'Discover: map the code paths in scope - list every file the change touches and every test that covers them.',
      'Plan: break the migration into small mechanical steps that each leave the build green.',
      'Execute: apply exactly one step, then commit it.',
      'Verify: run every check below. If a hard check fails, revert the step - do not patch forward.',
    ],
    verifier: [
      {
        check: 'Build and typecheck pass',
        how: 'The project build command exits 0, zero type errors',
        grade: 'hard',
      },
      {
        check: 'Full test suite green, with zero tests deleted or skipped',
        how: 'Test count after >= test count before; no new .skip / exclusions',
        grade: 'hard',
      },
      {
        check: 'Diff stays inside the allowlisted paths',
        how: 'git diff --stat touches only files named in the scope list',
        grade: 'hard',
      },
      {
        check: 'Behavior unchanged on the golden flows',
        how: 'The end-to-end / smoke tests for the flows this code serves pass unmodified',
        grade: 'hard',
      },
      {
        check: 'No new dependencies introduced',
        how: 'Lockfile diff is empty',
        grade: 'hard',
      },
      {
        check: 'Each commit is one nameable transformation',
        how: 'A reviewer can say what mechanical step each commit applied',
        grade: 'judgment',
      },
    ],
    tripwires: [
      'A test is deleted, skipped, or edited to make it pass - refactors change structure, never tests.',
      'The diff grows round over round instead of shrinking toward green.',
      'The same check fails 3 rounds in a row under different attempted fixes.',
    ],
    handback:
      'A PR with one commit per step, the before/after test counts, and the full check output attached.',
    recurringTrigger: 'Cron (e.g. nightly): pull the top migration task off the backlog.',
  },
  {
    id: 'bug-fixing',
    label: 'Bug fixing',
    icon: '🐛',
    description: 'Fix a reported bug at the root cause, with a regression test',
    goal: 'Fix the reported bug at its root cause and lock it in with a regression test that failed before the fix.',
    loopSteps: [
      'Discover: reproduce the bug and capture the exact failure output.',
      'Plan: trace the failure to its root cause, then write a repro test that fails for that reason.',
      'Execute: make the smallest change that turns the repro test green.',
      'Verify: run every check below. If the fix needs to grow, stop and re-diagnose instead.',
    ],
    verifier: [
      {
        check: 'A repro test exists that failed before the fix',
        how: 'The pre-fix failing output is captured in the run log - test first, fix second',
        grade: 'hard',
      },
      {
        check: 'The repro test now passes, and the full suite is green',
        how: 'Run the whole suite, not just the new test',
        grade: 'hard',
      },
      {
        check: 'The fix diff is minimal and on the failing code path',
        how: 'Roughly under 50 changed lines; no drive-by edits or reformatting',
        grade: 'hard',
      },
      {
        check: 'No files touched outside the failing code path and its tests',
        how: 'git diff --stat matches the diagnosed path',
        grade: 'hard',
      },
      {
        check: 'The root cause is named in one sentence',
        how: 'The hand-back explains why the bug happened - "symptom patched" is a fail',
        grade: 'judgment',
      },
    ],
    tripwires: [
      'About to commit a fix without a repro test that failed first.',
      'The same fix idea is being retried with cosmetic variations.',
      'The diff keeps growing - that is a refactor wearing a bug-fix label. Stop and report.',
    ],
    handback:
      'The PR, the repro test with its captured pre-fix failure, and the one-sentence root cause.',
    recurringTrigger: 'Webhook: an issue gets the bug label (or a support loop files a signal).',
  },
  {
    id: 'content-seo',
    label: 'Content / SEO writing',
    icon: '✍️',
    description: 'Draft publish-ready articles against a keyword brief',
    goal: 'Produce a publish-ready draft for the target keyword that clears every check and lands on an editor’s desk.',
    loopSteps: [
      'Discover: read the brief, then list the site’s existing pages on adjacent topics before writing a word.',
      'Plan: outline the piece so the searcher’s question is answered in the first screen.',
      'Execute: draft section by section, fetching and citing sources as you go - never from memory.',
      'Verify: run every check below, then queue the draft for the editor.',
    ],
    verifier: [
      {
        check: 'Target keyword in the title, H1, and first 100 words',
        how: 'Grep the draft for the exact phrase in all three places',
        grade: 'hard',
      },
      {
        check: 'Every statistic and factual claim links to a source fetched this run',
        how: 'Each citation URL appears in the run’s fetch log - no citation without a fetch',
        grade: 'hard',
      },
      {
        check: 'Word count inside the brief’s range',
        how: 'Count it; over or under the range is a fail, not a style choice',
        grade: 'hard',
      },
      {
        check: 'At least 2 internal links to pages that actually exist',
        how: 'Resolve each internal path against the site’s live routes',
        grade: 'hard',
      },
      {
        check: 'Meta title <= 60 characters, meta description <= 155',
        how: 'Character count on both fields',
        grade: 'hard',
      },
      {
        check: 'The intro alone tells the searcher what they will get',
        how: 'An editor reading only the first screen can restate the promise of the piece',
        grade: 'judgment',
      },
    ],
    tripwires: [
      'Any invented statistic, quote, or source - one fabrication kills the whole run.',
      'Keyword density creeping past ~2% - that is stuffing, and it reads like it.',
      'The draft substantially overlaps an existing page - cannibalization. Check slugs and titles first.',
    ],
    handback:
      'The draft, the source list with fetched URLs, and each check’s pass/fail - queued for a human editor.',
    recurringTrigger: 'Cron (e.g. weekday mornings): pull the top keyword off the content queue.',
  },
  {
    id: 'data-pipeline',
    label: 'Data pipeline',
    icon: '🧪',
    description: 'Transform data to a target schema with zero silent loss',
    goal: 'Transform the input data to the declared target schema with zero silent data loss.',
    loopSteps: [
      'Discover: profile the input - schema, row counts, null rates, and the ugly edge cases.',
      'Plan: write the mapping, the rejection rules, and the reconciliation math before running anything.',
      'Execute: dry-run on a sample first, then run the full input.',
      'Verify: run every check below. If reconciliation fails, fix the pipeline - never the check.',
    ],
    verifier: [
      {
        check: 'Output validates against the declared schema',
        how: 'Schema validator returns zero errors on the full output',
        grade: 'hard',
      },
      {
        check: 'Row counts reconcile exactly',
        how: 'Output rows + explicitly-rejected rows = input rows, and every rejection has a reason code',
        grade: 'hard',
      },
      {
        check: 'Re-running on the same input is idempotent',
        how: 'Second run produces byte-identical output (or an identical content hash)',
        grade: 'hard',
      },
      {
        check: 'A dry run on a sample completed before the full run',
        how: 'The sample run and its results are in the run log, timestamped before the full run',
        grade: 'hard',
      },
      {
        check: 'Rejected-row rate under the agreed threshold (e.g. 1%)',
        how: 'Above threshold = stop and report; partial data never ships silently',
        grade: 'hard',
      },
    ],
    tripwires: [
      'Any run against production data without a dry-run pass in this session.',
      'Counts do not reconcile and the "fix" on the table is relaxing the check.',
      'The schema gets edited to match the output, instead of the output fixed to match the schema.',
    ],
    handback:
      'The output location, the reconciliation table (in / out / rejected + reasons), and the idempotency hash.',
    recurringTrigger: 'Cron or event: new data lands in the input location.',
  },
  {
    id: 'research-report',
    label: 'Research / report',
    icon: '🔍',
    description: 'Answer a question with claims that survive source-checking',
    goal: 'Answer the research question with a report in which every claim survives source-checking.',
    loopSteps: [
      'Discover: decompose the question and fan out searches across independent sources.',
      'Plan: decide which claims the answer depends on, and what evidence each one needs.',
      'Execute: fetch sources and draft, citing each claim as it is written.',
      'Verify: run every check below. Cut any claim that fails - never soften the check to keep it.',
    ],
    verifier: [
      {
        check: 'Every claim carries a citation to a source fetched during this run',
        how: 'Each cited URL appears in the fetch log - citing from memory does not count',
        grade: 'hard',
      },
      {
        check: 'At least 3 independent sources',
        how: 'Sources that cite each other collapse into one - trace the chain',
        grade: 'hard',
      },
      {
        check: 'Direct quotes are verbatim',
        how: 'String-match every quote against the fetched source text',
        grade: 'hard',
      },
      {
        check: 'Disagreements between sources are surfaced, not averaged away',
        how: 'The report names who disagrees and why, instead of smoothing to a middle claim',
        grade: 'judgment',
      },
      {
        check: 'The first paragraph answers the original question',
        how: 'Not a topic overview - a reader stopping after paragraph one has the answer',
        grade: 'judgment',
      },
    ],
    tripwires: [
      'A claim that cannot be traced to a fetched source - cut it or fetch it, never keep it.',
      'All key claims trace back to a single origin - circular sourcing dressed up as consensus.',
      'The report keeps widening scope instead of answering the question that was asked.',
    ],
    handback:
      'The report, the full source list with fetch timestamps, and the claims that were cut for failing verification.',
    recurringTrigger: 'Cron (e.g. weekly): pull the next open question off the research backlog.',
  },
  {
    id: 'ui-building',
    label: 'UI building',
    icon: '🎨',
    description: 'Ship UI proven in a real browser, not by reading code',
    goal: 'Ship the specified UI, proven in a real browser - never by reading the code and assuming.',
    loopSteps: [
      'Discover: read the spec or mock, and list every view, state, and breakpoint it implies.',
      'Plan: map each requirement to the components in scope - and only those components.',
      'Execute: build one view or state at a time.',
      'Verify: render it in a real browser and run every check below. A screenshot is evidence; a code read is not.',
    ],
    verifier: [
      {
        check: 'Build passes and changed routes render without console errors',
        how: 'Dev server up, zero errors in the console on each changed route',
        grade: 'hard',
      },
      {
        check: 'A rendered screenshot or browser-automation run exists for every changed view',
        how: 'e.g. a Playwright run with captured screenshots or video attached to the PR',
        grade: 'hard',
      },
      {
        check: 'Accessibility score >= 95 on changed pages',
        how: 'Lighthouse or axe on each changed route',
        grade: 'hard',
      },
      {
        check: 'Layout holds at 375px, 768px, and 1280px',
        how: 'One screenshot per breakpoint, no horizontal overflow or collapsed layout',
        grade: 'hard',
      },
      {
        check: 'Diff touches only the components named in the spec',
        how: 'git diff --stat against the spec’s component list',
        grade: 'hard',
      },
      {
        check: 'The result matches the design intent, including hover / empty / error states',
        how: 'Judged against the spec or mock, state by state',
        grade: 'judgment',
      },
    ],
    tripwires: [
      'A view declared done with no rendered screenshot in the run data.',
      '!important or inline-style overrides accumulating to force a fix through.',
      'Edits leaking into shared components the spec never named.',
    ],
    handback:
      'The PR with per-breakpoint screenshots (or a browser-automation video) and the accessibility scores.',
    recurringTrigger: 'Webhook: a design task is labeled ready-to-build.',
  },
  {
    id: 'test-coverage',
    label: 'Test coverage',
    icon: '✅',
    description: 'Raise real coverage - tests that fail when the code breaks',
    goal: 'Raise real coverage on the target module - tests that actually fail when the code breaks.',
    loopSteps: [
      'Discover: read the coverage report and pick the highest-value uncovered behavior, not the easiest lines.',
      'Plan: for each behavior, decide the observable outcome the test should pin down.',
      'Execute: write the test, and confirm it fails when the behavior is intentionally broken.',
      'Verify: run every check below, including the flake check - three consecutive green runs.',
    ],
    verifier: [
      {
        check: 'Coverage on the target module at or above the target (line and branch)',
        how: 'Coverage report attached to the run data',
        grade: 'hard',
      },
      {
        check: 'Mutation spot-check passes',
        how: 'Intentionally break each newly-covered function once; at least one new test must fail each time',
        grade: 'hard',
      },
      {
        check: 'Full suite green 3 consecutive runs',
        how: 'No flaky tests introduced - three green runs back to back',
        grade: 'hard',
      },
      {
        check: 'Suite runtime stays within budget',
        how: 'e.g. total runtime grows by less than 20%',
        grade: 'hard',
      },
      {
        check: 'Tests assert behavior, not implementation',
        how: 'Renaming a private helper should not break them',
        grade: 'judgment',
      },
    ],
    tripwires: [
      'A test with no meaningful assertion, written to move the coverage number.',
      'Coverage "raised" by excluding files from the report.',
      'An existing failing test deleted or skipped to get to green.',
    ],
    handback:
      'The PR, the before/after coverage report, and the mutation spot-check results.',
    recurringTrigger: 'Cron (e.g. nightly): pick the least-covered module in the priority list.',
  },
  {
    id: 'ops-monitoring',
    label: 'Ops / monitoring',
    icon: '📟',
    description: 'Triage alerts with allowlisted, reversible actions only',
    goal: 'Triage alerts and keep the system healthy using only allowlisted, reversible actions.',
    loopSteps: [
      'Discover: pull the alert, its logs, and its metrics - read-only, before touching anything.',
      'Plan: form a hypothesis and pick the matching runbook action - or decide to escalate.',
      'Execute: apply one allowlisted action (or file the escalation).',
      'Verify: run every check below. Recovery means the metric recovered - not that the alert went quiet.',
    ],
    verifier: [
      {
        check: 'Every action taken is on the runbook allowlist',
        how: 'Anything not on the list requires an explicit human ack before it runs',
        grade: 'hard',
      },
      {
        check: 'Read-only diagnosis captured before any remediation',
        how: 'Logs, metrics, and the hypothesis are in the run data, timestamped first',
        grade: 'hard',
      },
      {
        check: 'Every closed alert has recovery evidence',
        how: 'The underlying metric returned to normal - silencing the alert does not count',
        grade: 'hard',
      },
      {
        check: 'Config or infra changes carry an explicit human approval',
        how: 'The approval is logged before the change executes',
        grade: 'hard',
      },
      {
        check: 'Escalations are decision-ready',
        how: 'One paragraph: symptom, evidence, hypothesis, recommended action',
        grade: 'judgment',
      },
    ],
    tripwires: [
      'The same remediation applied twice for the same alert - restarting it again is not a fix. Escalate.',
      'An alert closed without recovery evidence.',
      'Any write action outside the allowlist - immediate stop, not a judgment call.',
    ],
    handback:
      'The triage log: alert, diagnosis, action taken (or escalation), and recovery evidence.',
    recurringTrigger: 'Webhook: an alert fires (with a cron sweep for anything unacknowledged).',
  },
]

function formatChecklist(verifier: VerifierCheck[]): string {
  return verifier
    .map(
      (v) =>
        `  - [${v.grade === 'hard' ? 'HARD' : 'JUDGMENT'}] ${v.check}\n    (prove it: ${v.how})`
    )
    .join('\n')
}

export function buildLoopKit(taskId: string, cadenceId: Cadence, riskId: Risk): LoopKit {
  const task = TASK_TYPES.find((t) => t.id === taskId) ?? TASK_TYPES[0]
  const cadence = CADENCES.find((c) => c.id === cadenceId) ?? CADENCES[0]
  const risk = RISK_LEVELS.find((r) => r.id === riskId) ?? RISK_LEVELS[0]

  const loopType =
    risk.id === 'low'
      ? 'Closed loop with a small open edge: the hard checks are the floor, and one instruction may explore above it.'
      : 'Closed loop: success criteria pinned in advance, every step checked, explicit stop.'

  const lines: string[] = []

  lines.push(
    'You are running a closed loop. You do not decide when the work is good -',
    'the checks below decide. Your own judgment is never the verifier.',
    '',
    `Goal: ${task.goal}`,
    ''
  )

  if (cadence.id === 'recurring') {
    lines.push(
      `Trigger: ${task.recurringTrigger}`,
      '',
      'Before starting:',
      "  - Read this loop's contract (its README: goal, workflow, backlog, boundaries).",
      '  - Read the last 5 entries of the global work log.',
      ''
    )
  }

  lines.push('Loop (repeat until a stop condition holds):')
  task.loopSteps.forEach((step, i) => {
    lines.push(`  ${i + 1}. ${step}`)
  })
  lines.push(
    '',
    'Done when ALL of these pass:',
    formatChecklist(task.verifier),
    '',
    'Stop when:',
    '  - every check above passes, OR',
    `  - you have completed ${risk.iterationCap} rounds (then summarize what passed, what failed, and why), OR`,
    '  - a tripwire below fires.',
    '',
    'Tripwires (stop immediately and report - do not attempt to recover):'
  )
  task.tripwires.forEach((t) => {
    lines.push(`  - ${t}`)
  })
  lines.push(
    '  - No measurable progress for 2 consecutive rounds.',
    '',
    `Budget: hard cap ~$${risk.perRunUsd} for this run. Track spend as you go; if the next`,
    'round would exceed the cap, stop and report instead of continuing.'
  )
  if (cadence.id === 'recurring') {
    lines.push(
      `Monthly ceiling for this loop: ~$${risk.monthlyUsd}. Raise it only with run data, never mid-run.`
    )
  }
  lines.push('', `Ship rule (${risk.label.toLowerCase()} risk): ${risk.shipRule}`)

  if (cadence.id === 'recurring') {
    lines.push(
      '',
      'After finishing:',
      '  - Append what you did to the global work log.',
      '  - Write anything another loop should know as a signal artifact in the shared folder.',
      '  - If the queue was empty or the trigger was a false alarm, log "no-op" and exit',
      '    cleanly - a clean skip is a successful run.'
    )
  }

  lines.push(
    '',
    `Hand back: ${task.handback}`,
    'Always attach the run data: every check’s result, rounds used, and spend.'
  )

  return {
    task,
    cadence,
    risk,
    loopType,
    loopPrompt: lines.join('\n'),
  }
}

export function loopKitToMarkdown(kit: LoopKit): string {
  const { task, cadence, risk } = kit
  const md: string[] = []
  md.push(`# Agent Loop Kit: ${task.label}`)
  md.push('')
  md.push(`- **Cadence:** ${cadence.label}`)
  md.push(`- **Risk level:** ${risk.label} - ${risk.description}`)
  md.push(`- **Loop type:** ${kit.loopType}`)
  md.push('')
  md.push('## Verifier checklist')
  md.push('')
  task.verifier.forEach((v) => {
    md.push(`- [ ] **${v.check}** _(${v.grade})_ - ${v.how}`)
  })
  md.push('')
  md.push('## Stopping condition')
  md.push('')
  md.push(`All checks pass, or ${risk.iterationCap} rounds completed (then summarize and stop), or a tripwire fires.`)
  md.push('')
  md.push('## Budget cap')
  md.push('')
  md.push(`~$${risk.perRunUsd} hard cap per run.${cadence.id === 'recurring' ? ` ~$${risk.monthlyUsd}/month ceiling for the loop.` : ''} If the next round would exceed it, stop and report.`)
  md.push('')
  md.push('## Doom-loop tripwires')
  md.push('')
  task.tripwires.forEach((t) => {
    md.push(`- ${t}`)
  })
  md.push('- No measurable progress for 2 consecutive rounds.')
  md.push('')
  md.push(`## Ship rule (${risk.label.toLowerCase()} risk)`)
  md.push('')
  md.push(risk.shipRule)
  md.push('')
  md.push('## Loop prompt')
  md.push('')
  md.push('```text')
  md.push(kit.loopPrompt)
  md.push('```')
  md.push('')
  md.push('---')
  md.push('')
  md.push('Generated by the free [AI Agent Loop Builder](https://www.aibuilderclub.com/tools/agent-loop-builder) - based on the [Loop Engineering guide](https://www.aibuilderclub.com/blog/loop-engineering-guide-2026).')
  return md.join('\n')
}
